import React from 'react';
import { Dropdown, Input, MultiSelectDropdown } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ContactListType,
  WorkflowActionContactFieldType,
  WorkflowType,
} from 'shared/types';
import { SEARCHABLE_FIELDS_WFLOW_UPDATE_CONTACT } from '../../constants';

type ContactProps = {
  id?: string;
  disabled: boolean;
  allContactFields: {
    id: string;
    label: string | undefined;
    type: string;
    choices: {
      id: string;
      label: string;
    }[];
    field_type: number;
  }[];
  contactField: WorkflowActionContactFieldType;
  contactLists: ContactListType[];
  actionIndex: number;
  fieldIndex: number;
};

const Contact = (props: ContactProps) => {
  const {
    disabled,
    allContactFields,
    contactField,
    contactLists,
    actionIndex,
    fieldIndex,
  } = props;
  const { t, i18n } = useTranslation();
  const {
    watch,
    register,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const contact_fields = watch(
    `actions.${actionIndex}.action_meta.contact_fields`,
  );
  const value = watch(
    `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value`,
  );

  const selectedContactField = React.useMemo(() => {
    return allContactFields?.find((f) => f.id === contactField.field);
  }, [allContactFields, contactField]);

  const [selectedLists, setSelectedLists] = React.useState<ContactListType[]>(
    [],
  );
  React.useEffect(() => {
    if (contactLists && contactLists?.length > 0) {
      const selectedValues = value?.split(',') || [];
      const selectedValuesList =
        contactLists.filter((list) =>
          selectedValues?.includes(list.display_id),
        ) || [];
      setSelectedLists(selectedValuesList);
    }
  }, [contactLists]);

  const takenNames = React.useMemo(
    () => contactLists.map((cL) => cL.label),
    [contactLists],
  );

  return (
    <div className="wf-row" style={{ alignItems: 'baseline' }}>
      <Dropdown
        defaultSelectedItem={selectedContactField || null}
        width="320px"
        itemToString={(s) =>
          i18n.exists(`contacts.import_contact.default_fields.contact.${s.id}`)
            ? `${t(`contacts.import_contact.default_fields.contact.${s.id}`)}`
            : s.label || ''
        }
        itemComponent={(s) =>
          i18n.exists(`contacts.import_contact.default_fields.contact.${s.id}`)
            ? `${t(`contacts.import_contact.default_fields.contact.${s.id}`)}`
            : s.label || ''
        }
        emptyItemsText={`${t('no_results_found')}`}
        items={
          allContactFields?.filter(
            (contactField) =>
              !(contact_fields || [])
                .map((contactField) => contactField.field)
                .includes(contactField.id),
          ) || []
        }
        disabled={disabled}
        onChange={(item: { id: string; label: string | undefined }) => {
          setValue(
            `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.field`,
            item.id,
            { shouldValidate: true },
          );
          setValue(
            `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value`,
            '',
          );
        }}
        isErrored={
          submitCount > 0 &&
          !!get(
            errors,
            `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.field.message`,
          )
        }
        errorText={
          submitCount > 0
            ? get(
                errors,
                `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.field.message`,
              )
            : ''
        }
      />
      <div className="t-w-h-flex">
        {selectedContactField?.type === 'DROPDOWN' && (
          <Dropdown
            defaultSelectedItem={
              selectedContactField.choices.find((o) => o.id === value) || null
            }
            width="320px"
            type={
              SEARCHABLE_FIELDS_WFLOW_UPDATE_CONTACT.includes(
                selectedContactField.id,
              )
                ? 'input'
                : undefined
            }
            filterKey={
              SEARCHABLE_FIELDS_WFLOW_UPDATE_CONTACT.includes(
                selectedContactField.id,
              )
                ? 'label'
                : undefined
            }
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(i) => i.label}
            itemComponent={(i) => i.label}
            items={selectedContactField.choices}
            disabled={disabled}
            onChange={(item) => {
              // if check is required when clearing the input field using (X) icon
              if (item) {
                setValue(
                  `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value`,
                  item.id,
                  { shouldValidate: true },
                );
              }
            }}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
              )
            }
            errorText={
              submitCount > 0
                ? get(
                    errors,
                    `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
                  )
                : ''
            }
          />
        )}
        {selectedContactField?.type === 'MULTISELECT' && (
          <MultiSelectDropdown
            items={contactLists}
            placeholder={`${t('forms.placeholders.search_for_list')}`}
            id="addToLists"
            name="addToLists"
            selectedItem={selectedLists}
            maxAllowedLength={128}
            onCustomValueAdd={(val: ContactListType) => {
              if (takenNames.includes(val.label) || val?.label?.trim() === '')
                return;
            }}
            onChange={(val: ContactListType[]) => {
              setSelectedLists(val);
              const selectedListIds = val.map((list) => list.display_id);
              setValue(
                `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value`,
                selectedListIds?.toString(),
                { shouldValidate: true },
              );
            }}
            itemLabelIdentifier="label"
            itemValueIdentifier="display_id"
            disabled={disabled}
            isRequired
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
              )
            }
            errorText={
              submitCount > 0
                ? get(
                    errors,
                    `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
                  )
                : ''
            }
          />
        )}
        {selectedContactField?.type === 'TEXT' && (
          <Input
            {...register(
              `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value`,
            )}
            id="url"
            disabled={disabled}
            placeholder={`${t('forms.placeholders.enter_value')}`}
            isRequired
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
              )
            }
            errorText={
              submitCount > 0
                ? get(
                    errors,
                    `actions.${actionIndex}.action_meta.contact_fields.${fieldIndex}.value.message`,
                  )
                : ''
            }
          />
        )}
      </div>
    </div>
  );
};

export default Contact;
